import * as m from '@staiter/i18n';
import type { PercentCompleteMethod } from '@staiter/shared';
import type {
  DependencyType,
  EstimateUom,
  FieldType,
  ProjectKind,
  ProjectStatus,
  StateGroup,
  SubjectType,
  TaskPriority,
  TaskRefRole,
} from '@staiter/shared/task-module';

/**
 * Task-module i18n labels — тонкая Record-обёртка над `@staiter/i18n` (I1.1).
 *
 * Каждый словарь — объект с getter'ами: `DICT[value]` вызывает `m.*()` лениво,
 * поэтому подпись пересчитывается на текущую локаль при каждом рендере
 * (locale switch форсирует re-render провайдером i18n). Сигнатуры `Record<K, string>`
 * и `labelOf(DICT, value)` сохранены — потребители не трогаются.
 * Источник строк — `packages/i18n/messages/{ru,en}.json` (ключи `*_*`).
 */

export const TASK_PRIORITY_LABEL: Record<TaskPriority, string> = {
  get urgent() {
    return m.task_priority_urgent();
  },
  get high() {
    return m.task_priority_high();
  },
  get medium() {
    return m.task_priority_medium();
  },
  get low() {
    return m.task_priority_low();
  },
  get none() {
    return m.task_priority_none();
  },
};

/** AD8 — роли привязки (task_module_task_refs.role). */
export const TASK_REF_ROLE_LABEL: Record<TaskRefRole, string> = {
  get mentions() {
    return m.task_ref_role_mentions();
  },
  get affects() {
    return m.task_ref_role_affects();
  },
  get evidence() {
    return m.task_ref_role_evidence();
  },
};

export const STATE_GROUP_LABEL: Record<StateGroup, string> = {
  get backlog() {
    return m.state_group_backlog();
  },
  get unstarted() {
    return m.state_group_unstarted();
  },
  get started() {
    return m.state_group_started();
  },
  get paused() {
    return m.state_group_paused();
  },
  get completed() {
    return m.state_group_completed();
  },
  get cancelled() {
    return m.state_group_cancelled();
  },
  get triage() {
    return m.state_group_triage();
  },
};

export const SUBJECT_TYPE_LABEL: Record<SubjectType, string> = {
  get work_order() {
    return m.subject_type_work_order();
  },
  get job_card() {
    return m.subject_type_job_card();
  },
  get anomaly() {
    return m.subject_type_anomaly();
  },
  get source_conflict() {
    return m.subject_type_source_conflict();
  },
  get approval() {
    return m.subject_type_approval();
  },
  get stock_entry() {
    return m.subject_type_stock_entry();
  },
  get stock_reconciliation() {
    return m.subject_type_stock_reconciliation();
  },
  get invoice_ar() {
    return m.subject_type_invoice_ar();
  },
  get invoice_ap() {
    return m.subject_type_invoice_ap();
  },
  get payment() {
    return m.subject_type_payment();
  },
  get counterparty() {
    return m.subject_type_counterparty();
  },
  get item() {
    return m.subject_type_item();
  },
  get batch() {
    return m.subject_type_batch();
  },
  get period_close() {
    return m.subject_type_period_close();
  },
  get esf_filing() {
    return m.subject_type_esf_filing();
  },
  get kpn_filing() {
    return m.subject_type_kpn_filing();
  },
  get integration() {
    return m.subject_type_integration();
  },
};

export const DEPENDENCY_TYPE_LABEL: Record<DependencyType, string> = {
  get blocked_by() {
    return m.dependency_type_blocked_by();
  },
  get relates_to() {
    return m.dependency_type_relates_to();
  },
  get duplicate() {
    return m.dependency_type_duplicate();
  },
  get start_before() {
    return m.dependency_type_start_before();
  },
  get finish_before() {
    return m.dependency_type_finish_before();
  },
  get implemented_by() {
    return m.dependency_type_implemented_by();
  },
};

export const FIELD_TYPE_LABEL: Record<FieldType, string> = {
  get text() {
    return m.field_type_text();
  },
  get rich_text() {
    return m.field_type_rich_text();
  },
  get number() {
    return m.field_type_number();
  },
  get decimal() {
    return m.field_type_decimal();
  },
  get money() {
    return m.field_type_money();
  },
  get date() {
    return m.field_type_date();
  },
  get datetime() {
    return m.field_type_datetime();
  },
  get single_select() {
    return m.field_type_single_select();
  },
  get multi_select() {
    return m.field_type_multi_select();
  },
  get user_picker() {
    return m.field_type_user_picker();
  },
  get entity_ref() {
    return m.field_type_entity_ref();
  },
  get checkbox() {
    return m.field_type_checkbox();
  },
  get url() {
    return m.field_type_url();
  },
};

export const PROJECT_KIND_LABEL: Record<ProjectKind, string> = {
  get free() {
    return m.project_kind_free();
  },
  get work_order_execution() {
    return m.project_kind_work_order_execution();
  },
  get period_close() {
    return m.project_kind_period_close();
  },
  get onboarding_client() {
    return m.project_kind_onboarding_client();
  },
  get sku_launch() {
    return m.project_kind_sku_launch();
  },
  get warehouse_opening() {
    return m.project_kind_warehouse_opening();
  },
  get integration_setup() {
    return m.project_kind_integration_setup();
  },
  get audit_prep() {
    return m.project_kind_audit_prep();
  },
  get tax_filing() {
    return m.project_kind_tax_filing();
  },
  get physical_count() {
    return m.project_kind_physical_count();
  },
  get budget_cycle() {
    return m.project_kind_budget_cycle();
  },
  get production_sprint() {
    return m.project_kind_production_sprint();
  },
};

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  get planning() {
    return m.project_status_planning();
  },
  get active() {
    return m.project_status_active();
  },
  get on_hold() {
    return m.project_status_on_hold();
  },
  get completed() {
    return m.project_status_completed();
  },
  /**
   * B1 (project-actions-parity §1.1) — исход «Отменён». Ключ `project_status_cancelled`
   * уже был в обоих словарях (им подписывался легаси-вокабуляр `open|completed|cancelled`),
   * поэтому новых строк не заводим — забираем существующий в task-module-карту.
   */
  get cancelled() {
    return m.project_status_cancelled();
  },
  get archived() {
    return m.project_status_archived();
  },
};

export const ESTIMATE_UOM_LABEL: Record<EstimateUom, string> = {
  get hours() {
    return m.estimate_uom_hours();
  },
  get story_points() {
    return m.estimate_uom_story_points();
  },
  get days() {
    return m.estimate_uom_days();
  },
};

/**
 * Короткая форма единицы оценки — для подписи «число + единица».
 * Полная (`ESTIMATE_UOM_LABEL`) годится только отдельно стоящей: «40 часы»
 * по-русски неграмотно, нужно «40 ч».
 */
const ESTIMATE_UOM_SHORT: Record<string, () => string> = {
  hours: m.tm_hours_short,
  days: m.tm_days_short,
  story_points: () => 'sp',
};

/** «40 ч» / «3 sp». Единственная реализация — Kanban-карточка и просмотр шаблона. */
export function formatEstimate(value: string, uom: string | null): string {
  const n = Number(value);
  const num = Number.isFinite(n) ? String(n) : value;
  const suffix = uom ? (ESTIMATE_UOM_SHORT[uom]?.() ?? uom) : '';
  return suffix ? `${num} ${suffix}` : num;
}

export type DueBucket = 'overdue' | 'today' | 'this_week' | 'next_week' | 'later' | 'no_date';

/**
 * Due bucket labels (для virtual grouping канбана по срокам, FE-S12.2-19).
 */
export const DUE_BUCKET_LABEL: Record<DueBucket, string> = {
  get overdue() {
    return m.due_bucket_overdue();
  },
  get today() {
    return m.due_bucket_today();
  },
  get this_week() {
    return m.due_bucket_this_week();
  },
  get next_week() {
    return m.due_bucket_next_week();
  },
  get later() {
    return m.due_bucket_later();
  },
  get no_date() {
    return m.due_bucket_no_date();
  },
};

/**
 * Helper: type-safe lookup. Falls back to raw value if key missing
 * (defensive — для legacy data при миграции).
 */
export function labelOf<K extends string>(dict: Record<K, string>, key: K): string {
  return dict[key] ?? String(key);
}

/**
 * F4 (project-task-progress §3) — названия и формулы методов расчёта прогресса.
 *
 * Раньше подписи жили Record'ом с русскими литералами в `packages/shared`
 * (`PERCENT_COMPLETE_METHOD_LABEL`) — под en-локалью селектор говорил по-русски.
 * Геттеры реактивны к смене локали, как и остальные словари этого файла.
 *
 * `hint` — формула метода. Пользователь, выбирающий способ считать процент,
 * обязан видеть, ЧТО именно сложат: до этого пака селектор вообще ни на что не
 * влиял, и «доверять названию» было нечему.
 */
export const PERCENT_COMPLETE_METHOD_I18N: Record<
  PercentCompleteMethod,
  { readonly label: string; readonly hint: string }
> = {
  get manual() {
    return { label: m.pc_method_manual(), hint: m.pc_method_manual_hint() };
  },
  get task_completion() {
    return { label: m.pc_method_task_completion(), hint: m.pc_method_task_completion_hint() };
  },
  get task_progress() {
    return { label: m.pc_method_task_progress(), hint: m.pc_method_task_progress_hint() };
  },
  get task_weight() {
    return { label: m.pc_method_task_weight(), hint: m.pc_method_task_weight_hint() };
  },
};
